import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useEffect } from "react"
import { Navigate, Route, Routes, useNavigate, useParams } from "react-router"
import { api } from "./api/client"
import { AutoPilotShell } from "./components/autopilots/AutoPilotShell"
import { AutoPilotsLanding } from "./components/autopilots/AutoPilotsLanding"
import { getAP } from "./components/autopilots/registry"
import { RecordForm } from "./components/crud/RecordForm"
import { RecordDetailPanel } from "./components/detail/RecordDetailPanel"
import { CancelDiffDialog } from "./components/diff/CancelDiffDialog"
import { IngestionModal } from "./components/ingestion/IngestionModal"
import { RecordSelectionEditor } from "./components/ingestion/RecordSelectionEditor"
import { useStore } from "./store/useStore"

export default function App() {
  const { translate } = useTranslation()
  const {
    dbConfigured,
    setDbConfigured,
    configSwitching,
    actionState,
    cancelActionDialogOpen,
    closeCancelActionDialog,
    cancelAction,
    initHiddenScopes,
    setScopes,
    setTotalRecordCount,
    setScopeRecordCounts,
  } = useStore()

  useEffect(() => {
    Promise.all([
      api.listScopes(),
      api.scopeRecordCounts().catch(() => ({}) as Record<string, number>),
    ])
      .then(([scopes, counts]) => {
        // "Configured" means: scopes exist AND at least one carries records.
        // /admin/reset preserves Scope rows (shared across orgs by design)
        // but wipes primitives + records, so scopes.length alone would still
        // be > 0 after a reset. Without the totalRecords check the other
        // autopilots would stay unlocked on an empty DB — only the
        // Configuration AP (upload) should be reachable in that state.
        const totalRecords = Object.values(counts).reduce(
          (sum, n) => sum + n,
          0,
        )
        const configured = scopes.length > 0 && totalRecords > 0
        setDbConfigured(configured)
        if (configured) {
          setScopes(scopes)
          const hiddenIds = scopes.filter((s) => s.is_hidden).map((s) => s.id)
          initHiddenScopes(
            scopes.map((s) => s.id),
            counts,
            hiddenIds,
          )
          setTotalRecordCount(totalRecords)
          setScopeRecordCounts(counts)
        }
      })
      .catch(() => {
        setDbConfigured(false)
      })
  }, [
    setDbConfigured,
    setScopes,
    initHiddenScopes,
    setTotalRecordCount,
    setScopeRecordCounts,
  ])

  if (dbConfigured === null) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="space-y-3 text-center">
          <div className="relative mx-auto h-10 w-10">
            <div className="absolute inset-0 rounded-full border-4 border-grey4" />
            <div className="absolute inset-0 animate-spin rounded-full border-4 border-grey5 border-t-transparent" />
          </div>
          <p className="text-sm text-grey5">{translate("QMS/APP/LOADING")}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <Routes>
        <Route index element={<LandingRoute />} />
        <Route path=":apId" element={<APRoute />} />
      </Routes>

      {/* Overlays — always mounted regardless of active route */}
      <RecordDetailPanel />
      <RecordForm />
      <IngestionModal />
      <RecordSelectionEditor />

      {/* Config switching overlay */}
      {configSwitching && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-grey1/90">
          <div className="space-y-4 text-center">
            <div className="relative mx-auto h-12 w-12">
              <div className="absolute inset-0 rounded-full border-4 border-grey4" />
              <div className="absolute inset-0 animate-spin rounded-full border-4 border-yellow1 border-t-transparent" />
            </div>
            <div>
              <p className="text-lg font-medium text-white">
                {translate("QMS/APP/SWITCHING_TO", { name: configSwitching })}
              </p>
              <p className="mt-1 text-sm text-grey5">
                {translate("QMS/APP/RESETTING")}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Cancel/commit dialog for active action */}
      {cancelActionDialogOpen &&
        actionState &&
        Object.keys(actionState.diffs).length > 0 && (
          <CancelDiffDialog
            unresolvedCount={Object.values(actionState.diffs).reduce(
              (total, sd) => {
                return (
                  total +
                  Object.values(sd.records).filter((rec) => {
                    const hasUnresolvedCells = Object.values(rec.cells).some(
                      (c) => c.accepted === null,
                    )
                    const relDiffs = sd.relationDiffs?.[rec.recordId]
                    const hasUnresolvedRels = relDiffs
                      ? Object.values(relDiffs).some((rd) =>
                          rd.entries.some((e) => e.accepted === null),
                        )
                      : false
                    return hasUnresolvedCells || hasUnresolvedRels
                  }).length
                )
              },
              0,
            )}
            onRestore={() => {
              cancelAction()
            }}
            onApply={() => {
              closeCancelActionDialog()
              useStore.setState({
                _commitAcceptedSignal: Date.now(),
              } as Partial<ReturnType<typeof useStore.getState>>)
            }}
            onReturn={() => {
              closeCancelActionDialog()
            }}
          />
        )}
    </div>
  )
}

function LandingRoute() {
  const navigate = useNavigate()
  return <AutoPilotsLanding onSelect={(apId) => navigate(apId)} />
}

function APRoute() {
  const { apId } = useParams<{ apId: string }>()
  const navigate = useNavigate()
  const dbConfigured = useStore((s) => s.dbConfigured)
  const ap = getAP(apId ?? "")

  if (!ap) return <Navigate to="/qms-database" replace />
  if (!ap.unlockCondition({ dbConfigured }))
    return <Navigate to="/qms-database" replace />

  return <AutoPilotShell ap={ap} onBack={() => navigate("/qms-database")} />
}
